import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { PALETTE } from '../theme';

/**
 * Friendly empty-state surface used by both tools when nothing has happened
 * yet. Pairs an illustration with a short title + optional hint line.
 *
 *   <EmptyState
 *     image="/brand/empty-photo.png"
 *     title="Upload a photo to start"
 *     hint="Then pick a map shape to crop your photo into its silhouette."
 *   />
 */
const EmptyState = ({ image, alt = '', title, hint, action, sx = {} }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 2,
            py: { xs: 4, sm: 6 },
            minHeight: { xs: 280, sm: 360 },
            ...sx
        }}
    >
        {image && (
            <Box
                component="img"
                src={image}
                alt={alt}
                sx={{
                    width: { xs: 180, sm: 220 },
                    height: 'auto',
                    display: 'block',
                    mb: { xs: 2, sm: 2.5 },
                    // Soft-pull the illustration toward warm so it feels part
                    // of the palette even when generated separately.
                    filter: 'drop-shadow(0 8px 24px rgba(232, 99, 45, 0.10))'
                }}
            />
        )}
        <Typography
            sx={{
                fontWeight: 700,
                fontSize: { xs: '1.05rem', sm: '1.2rem' },
                color: PALETTE.text,
                mb: hint ? 0.75 : 0
            }}
        >
            {title}
        </Typography>
        {hint && (
            <Typography
                sx={{
                    color: PALETTE.textMuted,
                    fontSize: '0.95rem',
                    maxWidth: 360,
                    lineHeight: 1.5
                }}
            >
                {hint}
            </Typography>
        )}
        {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
);

EmptyState.propTypes = {
    image:  PropTypes.string,
    alt:    PropTypes.string,
    title:  PropTypes.node.isRequired,
    hint:   PropTypes.node,
    action: PropTypes.node,
    sx:     PropTypes.object
};

export default EmptyState;
